// ---------------------------------------------------------------
// Best-effort toll ESTIMATE from the rental's GPS route.
// We sample points along the trip, reverse-geocode them (OpenStreetMap), and
// flag toll-road names (turnpike / toll road / express-lane / managed-lane).
// This is a rough estimate for the agreement — it is NOT the exact toll charge.
// Exact, itemized tolls require a dedicated tolls API (e.g. TollGuru) tied to
// the vehicle's route; that's a paid integration for when the backend is set up.
// ---------------------------------------------------------------
import { getRouteReport, reverseGeocode } from './traccar';

// road names that reliably indicate a toll facility
const TOLL_RE = /\btoll\b|turnpike|tollway|express\s*lanes?|managed\s*lanes?|\bhot\s*lanes?\b/i;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// startAt/endAt ISO. avgPerToll = assumed $ per toll segment. maxSamples caps the
// geocode calls (OSM is rate-limited to ~1/sec, so this takes a few seconds).
export async function estimateTolls(deviceId, startAt, endAt, avgPerToll = 0.50, maxSamples = 15) {
  const route = await getRouteReport(deviceId, startAt, endAt || new Date().toISOString()).catch(() => []);
  if (!route || !route.length) return { list: [], count: 0, estCost: 0, sampled: 0 };
  const step = Math.max(1, Math.floor(route.length / maxSamples));
  const samples = route.filter((_, i) => i % step === 0).slice(0, maxSamples);
  const hits = [];
  for (const p of samples) {
    if (p.latitude == null) continue;
    const rev = await reverseGeocode(p.latitude, p.longitude).catch(() => null);
    await sleep(1100); // be polite to the geocoder's rate limit
    const name = rev ? (rev.road || rev.display || '') : '';
    if (name && TOLL_RE.test(name)) {
      const road = (rev && rev.road) || String(name).split(',')[0];
      // don't list the same road back-to-back
      if (!hits.length || hits[hits.length - 1].road !== road) {
        hits.push({ road, at: new Date(p.fixTime || p.deviceTime).toISOString() });
      }
    }
  }
  return { list: hits, count: hits.length, estCost: +(hits.length * avgPerToll).toFixed(2), sampled: samples.length };
}
